/**
 * @fileoverview A React component that displays a popup with a phone number and a QR code for calling a participant.
 * The component automatically refreshes the phone number every 5 minutes and updates the QR code.
 *
 * @example
 * function ParentComponent() {
 *   const participantId = '1234567890';
 *   return <CallPopup participantId={participantId} />;
 * }
 */

import { parsePhoneNumber } from 'libphonenumber-js'
import { DateTime, Duration } from 'luxon'
import { useEffect, useState } from 'react'
import QR from 'react-qr-code'
import { Tooltip } from 'react-tooltip'
import QuestionIcon from '../assets/question.svg'
import { useGetParticipantPhoneNumberQuery } from '../services/participants'
import { CallNoNumbers } from './CallNoNumbers'

/**
 * @interface CountdownProps
 * @description Props for the Countdown component.
 * @property {DateTime} expiry - The expiry date and time.
 * @property {() => void} onExpired - The function to call when the countdown expires.
 */
interface CountdownProps {
    expiry: DateTime
    onExpired: () => void
}

/**
 * @interface CallPopupProps
 * @description Props for the CallPopup component.
 * @property {string} participantId - The ID of the participant.
 */
interface CallPopupProps {
    participantId: string
}

/**
 * @function Countdown
 * @description Component for displaying a countdown timer.
 * @returns {JSX.Element | null} - The Countdown component.
 */
function Countdown({ expiry, onExpired }: CountdownProps) {
    const [difference, setDifference] = useState<Duration>(expiry.diffNow())

    useEffect(() => {
        const interval = setInterval(() => {
            const difference = expiry.diff(DateTime.local())
            if (difference.as('seconds') < 1) {
                onExpired()
            } else {
                setDifference(difference)
            }
        }, 500)

        return () => clearInterval(interval)
    }, [expiry, onExpired])

    return <span>{difference.toFormat('mm:ss')}</span>
}

/**
 * @function CallPopup
 * @description Component for displaying a popup with a phone number and a QR code for calling a participant.
 * @returns {JSX.Element | null} - The CallPopup component.
 */
export function CallPopup({ participantId }: CallPopupProps) {
    const [isExpired, setIsExpired] = useState<boolean>(false)
    const [expiryDate, setExpiryDate] = useState<DateTime>()
    const { data, isSuccess, error, refetch } = useGetParticipantPhoneNumberQuery(participantId, {
        refetchOnMountOrArgChange: true,
    })

    const phoneNumber = data?.phone

    useEffect(() => {
        if (isSuccess) {
            if (expiryDate?.diffNow().as('seconds') > 0) return
            const offset = Duration.fromObject({ seconds: data?.expire_after })
            setExpiryDate(DateTime.local().plus(offset))
        }
    }, [isSuccess])

    useEffect(() => {
        if (isExpired) {
            refetch()
            setIsExpired(!isExpired)
        }
    }, [participantId, isExpired])

    function formatPhoneNumber(phoneNumber: string | undefined) {
        if (!phoneNumber) return null

        const number = parsePhoneNumber(phoneNumber)
        return number.formatInternational()
    }

    if (error) {
        return <CallNoNumbers onRefresh={refetch} />
    }

    return (
        <div className="flex flex-col gap-y-6 p-6">
            <div className="flex flex-col gap-y-2">
                <span className="text-sm font-medium">Dial this phone number</span>
                <a
                    href={`tel:${phoneNumber}`}
                    className="text-xl font-medium text-indigo-700 hover:underline underline-offset-4"
                >
                    {formatPhoneNumber(phoneNumber)}
                </a>
                <span className="flex text-xs items-center gap-x-1.5">
                    This number will be valid for:{' '}
                    <span className="font-medium w-9">
                        {expiryDate ? (
                            <Countdown expiry={expiryDate} onExpired={() => setIsExpired(true)} />
                        ) : null}
                    </span>
                    <QuestionIcon
                        id={`call-tooltip`}
                        className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                    />
                    <Tooltip anchorSelect={`#call-tooltip`}>
                        <p className="w-40">
                            This is a temporary virtual number, not the participant&apos;s real
                            number. It refreshes every 5 minutes.
                        </p>
                    </Tooltip>
                </span>
            </div>
            <div className="relative flex justify-center items-center gap-x-2 text-gray-500">
                <div className="border-b border-gray-200 w-full"></div>
                <div className="font-medium text-xs">or</div>
                <div className="border-b border-gray-200 w-full"></div>
            </div>
            <div className="flex flex-col gap-y-3">
                <span className="text-sm font-medium">Scan the QR code to call</span>
                <QR size={232} className="p-6 border rounded-md" value={`tel:${phoneNumber}`} />
            </div>
        </div>
    )
}
